import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import type { TodoItemResponse, PublicTodoCompletedResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const DEFAULT_LIMIT = 100;

interface UseCompletedTimelineOptions {
  /** Authenticated mode — pass holderId + listId. */
  holderId?: string | null;
  listId?: string;
  /** Public mode — pass slug. */
  slug?: string;
  limit?: number;
}

interface UseCompletedTimelineReturn {
  items: TodoItemResponse[] | PublicTodoCompletedResponse['items'];
  loading: boolean;
  error: string | null;
}

/**
 * Lists completed items, either via the authenticated Vault-scoped
 * endpoint (holderId + listId) or via the public-slug surface (slug).
 * V-1 returns a fixed limit (100 by default); re-paginate as a follow-up
 * once the backend exposes an offset for the completed surface.
 */
export function useCompletedTimeline(
  options: UseCompletedTimelineOptions,
): UseCompletedTimelineReturn {
  const { holderId, listId, slug, limit } = options;

  const isPublic = !!slug;

  const queryKey = isPublic
    ? queryKeys.todoLists.publicCompleted(slug as string)
    : queryKeys.todoLists.completed(holderId ?? '', listId as string);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      if (isPublic) {
        return api.getPublicTodoListCompleted(slug as string, { limit: limit ?? DEFAULT_LIMIT });
      }
      return api.listTodoItems(holderId as string, listId as string, { completed: true });
    },
    enabled: isPublic ? !!slug : !!holderId && !!listId,
  });

  const items = data?.items ?? [];

  return {
    items,
    loading: isLoading,
    error: error instanceof Error ? error.message : error ? 'Failed to load completed items' : null,
  };
}
